import { useState, useEffect, useRef } from 'react'
import { Search, X, MapPin, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

interface GeocodingFeature {
  id: string
  place_name: string
  center: [number, number] // [lng, lat]
  place_type: string[]
  text: string
  context?: Array<{ id: string; text: string }>
}

interface GeocodingResponse {
  features: GeocodingFeature[]
}

interface AddressSearchProps {
  onLocationSelect: (lat: number, lng: number, placeName: string) => void
  workerUrl: string
  userLocation?: { latitude: number; longitude: number } | null
}

export function AddressSearch({ onLocationSelect, workerUrl, userLocation }: AddressSearchProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GeocodingFeature[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimer = useRef<NodeJS.Timeout>()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    if (query.trim().length < 3) {
      setResults([])
      setIsOpen(false)
      return
    }

    setIsLoading(true)

    debounceTimer.current = setTimeout(async () => {
      try {
        // Build API URL with proximity bias if user location available
        let apiUrl = `${workerUrl}/api/geocode?q=${encodeURIComponent(query)}`
        
        if (userLocation) {
          apiUrl += `&proximity=${userLocation.longitude},${userLocation.latitude}`
        }

        const response = await fetch(apiUrl)
        
        if (!response.ok) {
          throw new Error('Geocoding failed')
        }

        const data: GeocodingResponse = await response.json()
        setResults(data.features)
        setIsOpen(data.features.length > 0)
        setSelectedIndex(-1)
      } catch (error) {
        console.error('Search error:', error)
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }, 500) // 500ms debounce - increased to reduce API calls

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [query, workerUrl]) // Only trigger on query or workerUrl change, not userLocation

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        inputRef.current?.blur()
        break
    }
  }

  const handleSelect = (feature: GeocodingFeature) => {
    const [lng, lat] = feature.center
    onLocationSelect(lat, lng, feature.place_name)
    setQuery(feature.text)
    setIsOpen(false)
    inputRef.current?.blur()
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setIsOpen(false)
    inputRef.current?.focus()
  }

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
          <Search size={18} />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true)
          }}
          placeholder="Search for an address..."
          className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
        />

        {/* Loading or Clear Button */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 size={18} className="text-muted-foreground animate-spin" />
          ) : query.length > 0 ? (
            <button
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Results Dropdown */}
      <AnimatePresence>
        {isOpen && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-lg shadow-lg overflow-hidden z-50 max-h-[300px] overflow-y-auto"
          >
            {results.map((result, index) => (
              <button
                key={result.id}
                onClick={() => handleSelect(result)}
                className={`w-full px-4 py-3 text-left hover:bg-muted transition-colors flex items-start gap-3 ${
                  selectedIndex === index ? 'bg-muted' : ''
                }`}
              >
                <MapPin size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{result.text}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {result.place_name}
                  </div>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
